import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaEdit } from "react-icons/fa";
import { FaThumbsUp, FaShirt, FaEnvelope, FaQrcode } from "react-icons/fa6";
import { Dropdown, Form } from 'react-bootstrap';
import axios from 'axios';
import useUserRoles from '../../hooks/useUserRoles';
import EditTeamModal from './EditTeamModal';
import InvitePlayerModal from './InvitePlayerModal';
import QRCodeModal from './QRCodeModal';
import AddTeamToSeasonModal from './AddTeamToSeasonModal';
import TeamStatsTab from './TeamStatsTab';
import { useActiveLeague } from '../../App';
import { getSeasonOptionLabel } from '../utils/activeSeasonUtil';

// Custom hook for debouncing
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

// Utility function to truncate strings
const truncateString = (str, maxLength) => {
  if (!str) return '';
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + '...';
  }
  return str;
};

const CaptainActionsToggle = forwardRef(({ onClick, className }, ref) => {
  const safeClassName = (className || '')
    .split(' ')
    .filter((name) => name && name !== 'dropdown-toggle')
    .join(' ');

  return (
    <button
      type="button"
      ref={ref}
      className={`btn btn-sm btn-secondary ${safeClassName}`.trim()}
      onClick={(event) => {
        event.preventDefault();
        onClick(event);
      }}
    >
      <span className="d-inline-flex align-items-center">
        <span>Captain Actions</span>
        <i className="bi bi-chevron-down ms-2"></i>
      </span>
    </button>
  );
});

CaptainActionsToggle.displayName = 'CaptainActionsToggle';

function TeamDetailPage() {
  const { teamId } = useParams();
  const { activeSeason } = useActiveLeague();
  const [teamDetails, setTeamDetails] = useState(null);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [loading, setLoading] = useState(true);
  const [teamLoading, setTeamLoading] = useState(false);
  const [error, setError] = useState(null);
  const [games, setGames] = useState([]);
  const [user, setUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showAddToSeasonModal, setShowAddToSeasonModal] = useState(false);
  const { isCaptain } = useUserRoles();
  const [activeTab, setActiveTab] = useState("Schedule");
  const [teamStats, setTeamStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // Search and pagination state for Players tab
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const searchTimeoutRef = useRef(null);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    let cancelled = false;

    const loadTeamDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/api/teams/${teamId}/`, {
          withCredentials: true,
        });

        if (cancelled) {
          return;
        }

        const baseTeam = response.data;
        const attachedSeasons = baseTeam.attached_seasons || [];
        const defaultSeason = baseTeam.current_season?.toString() || attachedSeasons[0]?.id?.toString() || '';

        setSeasonOptions(attachedSeasons);
        setSelectedSeason(defaultSeason);

        if (defaultSeason) {
          const seasonResponse = await axios.get(`/api/teams/${teamId}/`, {
            params: { season: defaultSeason },
            withCredentials: true,
          });

          if (cancelled) {
            return;
          }

          setTeamDetails(seasonResponse.data);
          setSeasonOptions(seasonResponse.data.attached_seasons || attachedSeasons);
        } else {
          setTeamDetails(baseTeam);
        }
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        console.error('Error fetching team details:', loadError);
        setError('Failed to load team details.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTeamDetails();

    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const fetchTeamDetails = async (seasonId = selectedSeason) => {
    try {
      setTeamLoading(true);
      setError(null);
      const response = await axios.get(`/api/teams/${teamId}/`, {
        params: seasonId ? { season: seasonId } : {},
        withCredentials: true,
      });
      setTeamDetails(response.data);
      if (response.data.attached_seasons) {
        setSeasonOptions(response.data.attached_seasons);
      }
    } catch (fetchError) {
      console.error('Error fetching team details:', fetchError);
      setError('Failed to load team details.');
    } finally {
      setTeamLoading(false);
    }
  };

  const handleSeasonChange = async (event) => {
    const seasonId = event.target.value;
    setSelectedSeason(seasonId);
    if (seasonId) {
      await fetchTeamDetails(seasonId);
    }
  };

  useEffect(() => {
    // Fetch this team's games for the selected season
    if (!selectedSeason) {
      setGames([]);
      return;
    }

    axios.get(`/api/games/seasons/${selectedSeason}/`, {
      params: { is_played: false, team_id: teamId },
      withCredentials: true,
    })
      .then(response => {
        setGames(response.data);
      })
      .catch(error => {
        console.error('Error fetching games:', error);
      });
  }, [teamId, selectedSeason]);

  useEffect(() => {
    // One request covers every season, so switching seasons costs no network.
    let cancelled = false;

    setStatsLoading(true);
    setStatsError(null);

    axios.get(`/api/teams/${teamId}/stats/`, {
      withCredentials: true,
    })
      .then(response => {
        if (cancelled) return;
        setTeamStats(response.data);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Error fetching team stats:', error);
        setStatsError('Failed to load team stats.');
      })
      .finally(() => {
        if (!cancelled) {
          setStatsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [teamId]);

  useEffect(() => {
    axios.get('/api/users/my-details/', {
      withCredentials: true,
    })
      .then(response => {
        setUser(response.data);
      })
      .catch(error => {
        console.error('Error fetching user profile:', error);
      });
  }, []);

  // Filter and paginate players based on search term
  useEffect(() => {
    if (!teamDetails?.players) return;

    const filtered = teamDetails.players.filter(player => {
      const fullName = `${player.first_name} ${player.last_name}`.toLowerCase();
      const email = (player.email || '').toLowerCase();
      const phone = (player.phone_number || '').toLowerCase();
      const searchLower = debouncedSearchTerm.toLowerCase();

      return fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        phone.includes(searchLower);
    });

    setFilteredPlayers(filtered);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
    setCurrentPage(1);
  }, [debouncedSearchTerm, teamDetails?.players, pageSize]);

  // Get current page players
  const getCurrentPagePlayers = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredPlayers.slice(startIndex, endIndex);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for immediate visual feedback
    searchTimeoutRef.current = setTimeout(() => {
      // This is handled by the debounced search term
    }, 300);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleAttendingToggle = async (gameId, participantId, currentAttending) => {
    try {
      const response = await axios.patch(
        `/api/games/${gameId}/attending/`,
        { attending: !currentAttending },
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        setGames((prevGames) =>
          prevGames.map((game) =>
            game.id === gameId
              ? {
                ...game,
                players: game.players?.map((p) =>
                  p.id === participantId ? { ...p, attending: !currentAttending } : p
                ) || [],
              }
              : game
          )
        );
      }
    } catch (error) {
      console.error("Error updating attending status:", error);
    }
  };

  if (loading) {
    return <div className="container">Loading team details...</div>;
  }

  if (error) {
    return <div className="container text-danger">{error}</div>;
  }

  // Season-scoped record for the dropdown's current selection. Team.wins/losses/draws
  // on the team payload are stale denormalized columns and are not used here.
  const seasonStats = teamStats?.seasons?.find(
    (entry) => String(entry.season.id) === String(selectedSeason)
  ) || null;

  const activeSeasonLeagueId = activeSeason?.league?.id ?? activeSeason?.league ?? null;
  const teamLeagueId = teamDetails?.league ?? null;
  const showRegisterTeamForNewSeason =
    isCaptain &&
    activeSeason?.id != null &&
    activeSeasonLeagueId === teamLeagueId &&
    teamDetails?.current_season !== activeSeason.id;

  return (
    <>
      {/* Card START */}
      <div className="card mb-3 p-0">
        <div
          className="h-200px rounded-top"
          style={{
            backgroundImage: `url(/static/images/kb_static/lemont_rec_league_banner.png)`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Card body START */}
        <div className="card-body py-0">
          <div className="text-center">
            <div className="avatar avatar-xxl mt-n5 mb-3">
              <img
                className="avatar-img rounded-circle border border-white border-3"
                src={teamDetails.logo}
                alt={`${teamDetails.name} logo`}
                style={{ backgroundColor: 'white' }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between pb-3">
            <div className="flex-grow-1 text-center">
              <h3 className="mb-0">
                {teamDetails.name}
              </h3>
            </div>
            <div className="d-flex align-items-center">
              {isCaptain && (
                <Dropdown>
                  <Dropdown.Toggle as={CaptainActionsToggle} id="team-captain-actions">
                    Captain Actions
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item onClick={() => setShowEditModal(true)}>
                      <FaEdit className="me-1" /> Edit Team
                    </Dropdown.Item>
                    {showRegisterTeamForNewSeason && (
                      <Dropdown.Item onClick={() => setShowAddToSeasonModal(true)}>
                        <i className="bi bi-calendar-plus me-1"></i> Register Team for New Season
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={() => setShowInviteModal(true)}>
                      <FaEnvelope className="me-1" /> Invite Players
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowQRModal(true)}>
                      <FaQrcode className="me-1" /> QR Code
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
        </div>
      </div>

      <EditTeamModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        team={teamDetails}
        onTeamUpdated={async () => {
          setShowEditModal(false);
          await fetchTeamDetails(selectedSeason);
        }}
      />

      <AddTeamToSeasonModal
        show={showAddToSeasonModal}
        handleClose={() => setShowAddToSeasonModal(false)}
        team={teamDetails}
        onTeamUpdated={async (updatedTeam) => {
          setShowAddToSeasonModal(false);
          const nextSeason = updatedTeam?.current_season?.toString() || selectedSeason;
          setSelectedSeason(nextSeason);
          await fetchTeamDetails(nextSeason);
        }}
      />

      <InvitePlayerModal
        show={showInviteModal}
        handleClose={() => setShowInviteModal(false)}
        team={teamDetails}
        season={selectedSeason || teamDetails?.current_season}
      />

      <QRCodeModal
        show={showQRModal}
        handleClose={() => setShowQRModal(false)}
        team={teamDetails}
        season={selectedSeason || teamDetails?.current_season}
      />

      <div className="card card-body mb-3">
        <div className="row g-4">
          <div className="col-6 col-lg-4">
            <h5>Captain</h5>
            <div className="d-flex align-items-center">
              <span className="small m-2">
                {teamDetails.captain_name || 'No Captain'}
              </span>
              <img
                className="avatar-img rounded-circle me-2"
                src={
                  teamDetails.captain_picture
                    ? teamDetails.captain_picture
                    : `/static/images/kb_static/lemont_rec_leagues_circle.png`
                }
                alt={
                  teamDetails.captain_name
                    ? `${teamDetails.captain_name} avatar`
                    : 'Default avatar'
                }
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="col-6 col-lg-4">
            <h5>
              <span className="d-md-none">A-Captain</span>
              <span className="d-none d-md-inline">Assistant Captain</span>
            </h5>
            <div className="d-flex align-items-center">
              <span className="small m-2">
                {teamDetails.a_cap_name || 'None'}
              </span>
              <img
                className="avatar-img rounded-circle me-2"
                src={
                  teamDetails.a_cap_picture
                    ? teamDetails.a_cap_picture
                    : `/static/images/kb_static/lemont_rec_leagues_circle.png`
                }
                alt={
                  teamDetails.a_cap_name
                    ? `${teamDetails.a_cap_name} avatar`
                    : 'Default avatar'
                }
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="col-sm-6 col-lg-4">
            <h5>Team Color</h5>
            <p className="small mb-0"><FaShirt style={{ fontSize: '20px', color: teamDetails.color }}></FaShirt> {teamDetails.color_name}</p>
          </div>
        </div>
        <hr className="mt-4" />
        <div className="row align-items-center g-3">
          <div className="col-lg-4">
            <h5>Players</h5>
            <ul className="avatar-group list-unstyled align-items-center">
              {(teamDetails.players || [])
                .slice(0, 5)
                .map((p, idx) => (
                  <li key={`${p.id}-${idx}`} className="avatar avatar-xs">
                    <img
                      className="avatar-img rounded-circle"
                      src={
                        p.profile_picture ||
                        `/static/images/kb_static/lemont_rec_leagues_circle.png`
                      }
                      alt={`${p.first_name} ${p.last_name}`}
                      loading="lazy"
                    />
                  </li>
                ))
              }

              <li className="ms-2 ms-md-4">
                <small>
                  {teamDetails.players?.length || 0}{' '}
                  {teamDetails.players?.length === 1 ? 'team member' : 'team members'}
                </small>
              </li>
            </ul>
          </div>
          <div className="col-md-6 col-lg-3">
            <Form.Group controlId="profile-team-season-select" className="kb-season-select">
              <Form.Label>Season</Form.Label>
              <Form.Select
                value={selectedSeason}
                onChange={handleSeasonChange}
                disabled={teamLoading || seasonOptions.length === 0}
              >
                {seasonOptions.length === 0 ? (
                  <option value="">No seasons</option>
                ) : (
                  seasonOptions.map((season) => (
                    <option key={season.id} value={season.id}>
                      {getSeasonOptionLabel(season)}
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-8 col-lg-5">
            <div className="kb-record">
              <div className="kb-record__item kb-record__item--win">
                <span className="kb-record__label">Wins</span>
                <div className="kb-record__value">
                  <i className="bi bi-trophy"></i>
                  <h5 className="mb-0">{seasonStats?.wins ?? 0}</h5>
                </div>
              </div>
              <div className="kb-record__item kb-record__item--loss">
                <span className="kb-record__label">Losses</span>
                <div className="kb-record__value">
                  <i className="bi bi-hand-thumbs-down"></i>
                  <h5 className="mb-0">{seasonStats?.losses ?? 0}</h5>
                </div>
              </div>
              <div className="kb-record__item kb-record__item--draw">
                <span className="kb-record__label">Draws</span>
                <div className="kb-record__value">
                  <i className="bi bi-people"></i>
                  <h5 className="mb-0">{seasonStats?.draws ?? 0}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="card">
        <div className="card-header border-0 pb-0">
          <ul className="nav nav-tabs nav-bottom-line justify-content-center justify-content-md-start">
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "Schedule" ? "active" : ""}`}
                href="#schedule"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("Schedule");
                }}
              >
                Schedule
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "Players" ? "active" : ""}`}
                href="#players"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("Players");
                }}
              >
                Players
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "Stats" ? "active" : ""}`}
                href="#stats"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("Stats");
                }}
              >
                Stats
              </a>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div className="card-body">
          <div className="tab-content">
            {/* Schedule Tab */}
            <div className={`tab-pane fade ${activeTab === "Schedule" ? "show active" : ""}`} id="schedule">
              <div className="row g-4">
                {games.length > 0 ? (
                  games.map((game) => {
                    // Extract team participants from the new API structure
                    const homeParticipant = game.teams?.find((p) => p.is_home) || {};
                    const awayParticipant = game.teams?.find((p) => p.is_away) || {};
                    const userParticipant = game.players?.find(
                      (p) => user?.players?.some(player => player.id === p.object_id)
                    ) || {};

                    return (
                      <div key={game.id} className="col-12">
                        <div className="row align-items-center">
                          <div className="col-6 col-md-auto order-1 d-flex align-items-center">
                            <div className="avatar avatar-xl d-flex align-items-center">
                              <div className="position-relative">
                                <div
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    margin: 'auto',
                                    display: 'block',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: '8px',
                                  }}
                                >
                                  {homeParticipant.participant_logo ? (
                                    <img
                                      src={homeParticipant.participant_logo}
                                      alt={`${homeParticipant.participant_name} logo`}
                                      style={{
                                        width: '50px',
                                        height: '50px',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
                                      }}
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '50%',
                                        backgroundColor: 'blue',
                                        position: 'absolute',
                                        top: 0,
                                      }}
                                    ></div>
                                  )}

                                  {awayParticipant.participant_logo ? (
                                    <img
                                      src={awayParticipant.participant_logo}
                                      alt={`${awayParticipant.participant_name} logo`}
                                      style={{
                                        width: '50px',
                                        height: '50px',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        left: 0,
                                        bottom: 0,
                                        clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
                                      }}
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '50%',
                                        backgroundColor: 'red',
                                        position: 'absolute',
                                        bottom: 0,
                                      }}
                                    ></div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md order-3 order-md-2">
                            <h5 className="mb-1">
                              <Link className="nav-link" to={`/games/${game.id}`}>
                                {game.home_team} vs. {game.away_team}
                              </Link>
                            </h5>
                            <ul className="nav nav-stack small">
                              <li className="nav-item">
                                <i className="bi bi-calendar-check pe-1"></i> {game.scheduled_date_only} at {game.scheduled_time_only}
                              </li>
                              <li className="nav-item">
                                <i className="bi bi-geo-alt pe-1"></i> {game.location.name}
                              </li>
                            </ul>
                          </div>
                          <div className="col-6 col-md-auto order-2 order-md-3 d-flex align-items-center justify-content-end">
                            <input
                              type="checkbox"
                              className="btn-check"
                              id={`Interested-${userParticipant.id}`}
                              checked={userParticipant.attending || false}
                              onChange={() => handleAttendingToggle(game.id, userParticipant.id, userParticipant.attending)}
                            />
                            <label
                              className={`btn btn-sm ${userParticipant.attending ? 'btn-success' : 'btn-outline-success'} text-nowrap`}
                              htmlFor={`Interested-${userParticipant.id}`}
                              style={{ whiteSpace: 'nowrap', maxWidth: '100%' }}
                            >
                              <FaThumbsUp className="me-1" /> {userParticipant.attending ? "Attending" : "Not Attending"}
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p>No upcoming games found.</p>
                )}
              </div>
            </div>

            {/* Players Tab */}
            <div className={`tab-pane fade ${activeTab === "Players" ? "show active" : ""}`} id="players">
              {/* Search and Controls */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search players..."
                      value={searchTerm}
                      onChange={handleSearch}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={30}>30 per page</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <div className="text-end">
                    <small className="text-muted">
                      Showing {totalCount} players
                    </small>
                  </div>
                </div>
              </div>

              {/* Players Grid */}
              <div className="row g-4">
                {getCurrentPagePlayers().map((player) => (
                  <div key={player.id} className="col-12 col-sm-6">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center mb-3">
                            <img
                              src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                              alt={`${player.first_name} ${player.last_name}`}
                              className="rounded-circle me-3"
                              style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                            />
                            <div className="flex-grow-1">
                              <h6 className="mb-1">
                                {truncateString(player.first_name + ' ' + player.last_name, 20)}
                              </h6>
                              <small className="text-muted">
                                {teamDetails.captain === player.id ? 'Captain' :
                                  teamDetails.a_cap === player.id ? 'Assistant Captain' :
                                    'Player'}
                              </small>
                            </div>
                          </div>
                          <div className="mt-auto">
                            {player.email && (
                              <div className="mb-2">
                                <small className="text-muted">
                                  <i className="bi bi-envelope me-1"></i>
                                  <a href={`mailto:${player.email}`} className="text-decoration-none">
                                    {truncateString(player.email, 35)}
                                  </a>
                                </small>
                              </div>
                            )}
                            <div className="mb-2">
                              <small className="text-muted">
                                <i className="bi bi-telephone me-1"></i>
                                <a href={`tel:${player.phone_number}`} className="text-decoration-none">
                                  {player.phone_number && isCaptain ? truncateString(player.phone_number, 35) : 'N/A'}
                                </a>
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {getCurrentPagePlayers().length === 0 && (
                  <div className="col-12">
                    <div className="text-center text-muted py-4">
                      {searchTerm ? 'No players found matching your search' : 'No players found'}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile-Friendly Pagination */}
              {totalPages > 1 && (
                <div className="d-flex flex-column align-items-center mt-4">
                  <div className="text-center mb-3">
                    <span className="text-muted">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} players
                    </span>
                  </div>
                  <nav aria-label="Page navigation">
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      </li>

                      {/* Show page numbers with ellipsis for mobile */}
                      {totalPages <= 5 ? (
                        // Show all pages if 5 or fewer
                        [...Array(totalPages)].map((_, index) => {
                          const pageNum = index + 1;
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })
                      ) : (
                        // Show smart pagination for mobile
                        <>
                          {/* First page */}
                          <li className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(1)}
                            >
                              1
                            </button>
                          </li>

                          {/* Ellipsis if needed */}
                          {currentPage > 3 && (
                            <li className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          )}

                          {/* Current page and neighbors */}
                          {currentPage > 2 && currentPage < totalPages && (
                            <li className="page-item">
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                              >
                                {currentPage - 1}
                              </button>
                            </li>
                          )}

                          {currentPage !== 1 && currentPage !== totalPages && (
                            <li className="page-item active">
                              <button className="page-link">
                                {currentPage}
                              </button>
                            </li>
                          )}

                          {currentPage < totalPages - 1 && currentPage > 1 && (
                            <li className="page-item">
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                              >
                                {currentPage + 1}
                              </button>
                            </li>
                          )}

                          {/* Ellipsis if needed */}
                          {currentPage < totalPages - 2 && (
                            <li className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          )}

                          {/* Last page */}
                          <li className={`page-item ${currentPage === totalPages ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                            >
                              {totalPages}
                            </button>
                          </li>
                        </>
                      )}

                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>

            {/* Stats Tab */}
            <div className={`tab-pane fade ${activeTab === "Stats" ? "show active" : ""}`} id="stats">
              <TeamStatsTab
                stats={teamStats}
                seasonStats={seasonStats}
                selectedSeason={selectedSeason}
                loading={statsLoading}
                error={statsError}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TeamDetailPage;
